<script setup lang="ts">
    interface Props {
        readonly navigation?: FirstLayerLinkStoryblok[];
        readonly isDesktop?: boolean;
    }

    const { navigation = [], isDesktop = true } = defineProps<Props>();
    const { disableSmoothScroll } = useDisableSmoothScroll();

    const { data: story } = useConfig();

    const { isAuthenticated, isLocked, loginModalController, wrapNavigate } = useLockLinkGuard({
        onAllowed: () => closeSecondLayer(),
        story
    });

    const isClient = useClientGate(true);
    const activeItem = ref<string | null>(null);
    const hoveredItem = ref<string | null>(null);
    const focusedItem = ref<string | null>(null);
    const hoverDelayComplete = ref(false);
    // Preserve the initial hover delay without making an invisible panel interactive.
    const openingDelayMilliseconds = 400;
    let openingTimer: ReturnType<typeof setTimeout> | undefined;

    const cancelOpening = () => {
        clearTimeout(openingTimer);
        openingTimer = undefined;
    };

    const enterItem = (identifier: string) => {
        cancelOpening();
        hoveredItem.value = identifier;

        if (hoverDelayComplete.value || activeItem.value !== null) {
            activeItem.value = identifier;
            return;
        }

        openingTimer = setTimeout(() => {
            hoverDelayComplete.value = true;
            activeItem.value = hoveredItem.value;
            openingTimer = undefined;
        }, openingDelayMilliseconds);
    };

    const leaveItem = (identifier: string) => {
        if (hoveredItem.value === identifier) {
            hoveredItem.value = null;
            cancelOpening();
        }
        if (activeItem.value === identifier && focusedItem.value !== identifier) {
            activeItem.value = null;
        }
    };

    const focusItem = (identifier: string) => {
        cancelOpening();
        focusedItem.value = identifier;
        activeItem.value = identifier;
    };

    const leaveItemFocus = (event: FocusEvent, identifier: string) => {
        const item = event.currentTarget as HTMLElement;
        if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

        focusedItem.value = null;
        if (activeItem.value === identifier && hoveredItem.value !== identifier) {
            activeItem.value = null;
        }
    };

    const dismissSecondLayer = () => {
        cancelOpening();
        activeItem.value = null;
    };

    const handleEscape = (event: KeyboardEvent) => {
        const item = event.currentTarget as HTMLElement;
        item.querySelector<HTMLElement>('.first-layer-link')?.focus();
        dismissSecondLayer();
    };

    const handleSecondLayerLinkClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        const anchor = target?.closest('a');
        if (!anchor) return;
        if (anchor.getAttribute('aria-disabled') === 'true') return;

        dismissSecondLayer();
        disableSmoothScroll();
    };

    const closeSecondLayer = () => {
        dismissSecondLayer();

        disableSmoothScroll();
    };

    const handleFirstLayerFocusSwitch = (event: KeyboardEvent) => {
        const currentTarget = event.currentTarget as HTMLElement;
        const key = event.key;

        let sibling: null | HTMLElement = null;
        if (key === 'ArrowLeft') {
            sibling = currentTarget.parentElement?.previousElementSibling as HTMLElement | null;
        } else if (key === 'ArrowRight') {
            sibling = currentTarget.parentElement?.nextElementSibling as HTMLElement | null;
        }

        if (sibling?.firstElementChild instanceof HTMLElement) {
            event.preventDefault();
            sibling.firstElementChild.focus();
        }
    };

    const needsIndicator = ({ secondLayer, additionalBlocks }: FirstLayerLinkStoryblok) => {
        return (secondLayer && secondLayer.length > 0) || (additionalBlocks && additionalBlocks.length > 0);
    };

    const route = useRoute();
    watch(() => route.fullPath, dismissSecondLayer);
    watch(() => isDesktop, dismissSecondLayer);
    onUnmounted(cancelOpening);
</script>
<template>
    <nav class="navigation-desktop">
        <ul
            class="first-layer"
            role="menubar"
            aria-label="Medice Navigation"
            @mouseleave="hoverDelayComplete = false"
        >
            <li
                v-for="firstLayer in navigation"
                :key="firstLayer._uid"
                role="menuitem"
                class="first-layer-element"
                :class="[
                    {
                        '--is-highlighted': firstLayer.isHighLighted
                    }
                ]"
                @mouseenter="enterItem(firstLayer._uid)"
                @mouseleave="leaveItem(firstLayer._uid)"
                @focusin="focusItem(firstLayer._uid)"
                @focusout="leaveItemFocus($event, firstLayer._uid)"
                @keydown.esc.stop.prevent="handleEscape"
            >
                <NuxtLink
                    v-if="firstLayer.link"
                    v-slot="{ href, navigate }"
                    :to="useStoryblokUrl(firstLayer.link)"
                    custom
                >
                    <a
                        :href="href"
                        :target="firstLayer.External || firstLayer.link?.target === '_blank' ? '_blank' : ''"
                        class="first-layer-link"
                        data-testid="navigation-first-layer-link"
                        :class="{
                            '--with-indicator': needsIndicator(firstLayer),
                            '--hover': activeItem === firstLayer._uid
                        }"
                        :aria-expanded="needsIndicator(firstLayer) ? activeItem === firstLayer._uid : undefined"
                        @keydown="handleFirstLayerFocusSwitch"
                        @click="wrapNavigate(navigate, firstLayer.link).onClick($event)"
                        @keydown.enter="wrapNavigate(navigate, firstLayer.link).onEnter($event)"
                        @keydown.space="wrapNavigate(navigate, firstLayer.link).onSpace($event)"
                    >
                        <span>
                            {{ firstLayer.linkName }}
                            <img
                                v-if="isClient && firstLayer.link?.story?.content?.locked && !isAuthenticated"
                                src="~/assets/icons/lock-keyhole.svg"
                                class="navi-lock first-layer-lock"
                                aria-hidden="true"
                                alt=""
                            />
                            <svg
                                v-if="needsIndicator(firstLayer)"
                                class="indicator"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-label="indicator"
                                aria-hidden="true"
                            >
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </span>
                    </a>
                </NuxtLink>
                <div
                    v-if="firstLayer.secondLayer?.length || firstLayer.additionalBlocks?.length"
                    class="second-layer"
                    :class="{ '--show': activeItem === firstLayer._uid }"
                    @click.capture="handleSecondLayerLinkClick"
                >
                    <div
                        v-if="isDesktop"
                        class="second-layer-grid"
                    >
                        <div
                            v-if="firstLayer.secondLayer?.length"
                            class="second-layer-links-wrapper"
                            :class="{ '--alternate-layout': firstLayer.alternateLinkLayoutDirection }"
                        >
                            <ul
                                v-for="secondLayer in firstLayer.secondLayer"
                                :key="secondLayer._uid"
                                class="second-layer-links"
                                :class="{ '--alternate-layout': firstLayer.alternateLinkLayoutDirection }"
                            >
                                <li
                                    v-for="secondLayerLink in secondLayer.links"
                                    :key="secondLayerLink._uid"
                                >
                                    <NuxtLink
                                        v-if="secondLayerLink?.link?.story?.full_slug"
                                        v-slot="{ href, navigate }"
                                        :to="useStoryblokUrl(secondLayerLink.link)"
                                        custom
                                    >
                                        <a
                                            :href="href"
                                            class="second-layer-link"
                                            data-testid="navigation-second-layer-link"
                                            :aria-disabled="
                                                isClient && isLocked(secondLayerLink.link) && !isAuthenticated
                                            "
                                            @click="wrapNavigate(navigate, secondLayerLink.link).onClick($event)"
                                            @keydown.enter="
                                                wrapNavigate(navigate, secondLayerLink.link).onEnter($event)
                                            "
                                            @keydown.space="
                                                wrapNavigate(navigate, secondLayerLink.link).onSpace($event)
                                            "
                                        >
                                            {{ secondLayerLink.linkName }}

                                            <template
                                                v-if="
                                                    secondLayerLink.link.story?.full_slug ||
                                                    secondLayerLink.link.cached_url
                                                "
                                            >
                                                <img
                                                    v-if="
                                                        isClient &&
                                                        secondLayerLink.link.story?.content?.locked &&
                                                        !isAuthenticated
                                                    "
                                                    src="~/assets/icons/lock-keyhole.svg"
                                                    class="navi-lock"
                                                    aria-hidden="true"
                                                />
                                                <img
                                                    v-else
                                                    src="~/assets/icons/chevron-right-black-thicker.svg"
                                                    alt="chevron"
                                                    aria-hidden="true"
                                                />
                                            </template>
                                        </a>
                                    </NuxtLink>

                                    <div
                                        v-else
                                        class="second-layer-link"
                                    >
                                        {{ secondLayerLink.linkName }}
                                    </div>
                                    <ul
                                        v-if="secondLayerLink.thirdLayer?.length"
                                        class="third-layer"
                                    >
                                        <li
                                            v-for="thirdLayerLink in secondLayerLink.thirdLayer"
                                            :key="thirdLayerLink._uid"
                                        >
                                            <NuxtLink
                                                v-if="
                                                    thirdLayerLink.component === 'ThirdLayerLink' &&
                                                    'link' in thirdLayerLink &&
                                                    thirdLayerLink.link
                                                "
                                                v-slot="{ href, navigate }"
                                                custom
                                                :to="useStoryblokUrl(thirdLayerLink.link)"
                                            >
                                                <a
                                                    :href="href"
                                                    class="third-layer-link"
                                                    data-testid="navigation-third-layer-link"
                                                    custom
                                                    :aria-disabled="
                                                        isClient && isLocked(thirdLayerLink.link) && !isAuthenticated
                                                    "
                                                    @click="wrapNavigate(navigate, thirdLayerLink.link).onClick($event)"
                                                    @keydown.enter="
                                                        wrapNavigate(navigate, thirdLayerLink.link).onEnter($event)
                                                    "
                                                    @keydown.space="
                                                        wrapNavigate(navigate, thirdLayerLink.link).onSpace($event)
                                                    "
                                                >
                                                    {{ thirdLayerLink.linkName }}

                                                    <img
                                                        v-if="
                                                            isClient &&
                                                            thirdLayerLink.link?.story?.content?.locked &&
                                                            !isAuthenticated
                                                        "
                                                        src="~/assets/icons/lock-keyhole.svg"
                                                        class="navi-lock"
                                                        aria-hidden="true"
                                                    />
                                                </a>
                                            </NuxtLink>

                                            <StoryblokComponentWrapper
                                                v-else
                                                :blok="thirdLayerLink"
                                                class="third-layer-link"
                                                @click="closeSecondLayer"
                                            />
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <ul
                            v-if="firstLayer.additionalBlocks?.length"
                            class="additional-blocks"
                            data-testid="navigation-additional-blocks"
                            :class="{
                                '--left': firstLayer.additionalBlocksLeft,
                                '--full-width': firstLayer.secondLayer?.length == 0
                            }"
                        >
                            <li
                                v-for="additionalBlock in firstLayer.additionalBlocks"
                                :key="additionalBlock._uid"
                            >
                                <StoryblokComponentWrapper :blok="additionalBlock" />
                            </li>
                        </ul>
                    </div>
                </div>
            </li>
        </ul>
    </nav>

    <SharedLoginModal
        :login-modal-controller="loginModalController"
        modal-id="header-navigation-desktop-login"
    />
</template>

<style scoped>
    @import '~/assets/scss/navigation/header-navigation-desktop.scss';
</style>
